/* eslint-disable import/order */
/**
 * AppRouter Component
 * Main application routing configuration for Falcon Terminal
 * Single SPA routing for all atomic apps
 */

import React from "react";

import { Routes, Route, Navigate } from "react-router-dom";

// Shared feature pages
import { AuthPage } from "@/features/auth/pages/AuthPage";
import { AUTH_ROUTES } from "@/features/auth/routes";
import { TerminalPage } from "@/features/terminal/pages/TerminalPage";
import { TERMINAL_ROUTES } from "@/features/terminal/routes";

// Atomic App pages
import { AnalysisHomePage } from "@apps/falcon-analysis/features/analysis/pages/AnalysisHomePage";
import { ANALYSIS_ROUTES } from "@apps/falcon-analysis/features/analysis/routes";
import { INNOVATION_ROUTES } from "@apps/falcon-innovation-studio/routes";
import { StrategyHomePage } from "@apps/strategy-studio/features/strategy/pages/StrategyHomePage";
import {
  EXPERIMENT_ROUTES,
} from "@apps/falcon-innovation-studio/features/experiments/routes";

// Atomic App route constants
import { STRATEGY_ROUTES } from "@apps/strategy-studio/features/strategy/routes";

import { PrivateRoute } from "./PrivateRoute";
import { PublicRoute } from "./PublicRoute";
import { ExperimentEditorLayout } from "@/apps/falcon-innovation-studio/features/experiments/components/ExperimentEditorLayout";
import ChaseCreditCardDemoPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ChaseCreditCardDemoPage";
import ExperimentContextPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentContextPage";
import ExperimentCustomerPreviewPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentCustomerPreviewPage";
import ExperimentFinalizePage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentFinalizePage";
import ExperimentPrototypePage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentPrototypePage";
import ExperimentReportPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentReportPage";
import ExperimentResearchPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentResearchPage";
import ExperimentSetupRedirectPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentSetupRedirectPage";
import ExperimentTaggingPage from "@/apps/falcon-innovation-studio/features/experiments/pages/ExperimentTaggingPage";
import DashboardHomePage from "@/apps/falcon-innovation-studio/features/dashboard/pages/DashboardHomePage";
import { MainLayout } from "@/shared/components/layout";

export const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Root redirect */}
      <Route
        path="/"
        element={<Navigate to={TERMINAL_ROUTES.HOME} replace />}
      />

      {/* Public routes */}
      <Route element={<PublicRoute />}>
        <Route path={AUTH_ROUTES.LOGIN} element={<AuthPage />} />
      </Route>

      {/* Public demo routes (used as iframe targets for prototype previews).
          Splat route lets the Chase demo respond to per-step subpaths
          (/goal, /verify, /offer, /terms, /done) which the tagging
          workspace navigates to. */}
      <Route
        path="/demo/chase-credit-card-application"
        element={<ChaseCreditCardDemoPage />}
      />
      <Route
        path="/demo/chase-credit-card-application/*"
        element={<ChaseCreditCardDemoPage />}
      />

      {/* Protected routes */}
      <Route element={<PrivateRoute />}>
        {/* Terminal - Studio Selector */}
        <Route path={TERMINAL_ROUTES.HOME} element={<TerminalPage />} />

        <Route element={<MainLayout />}>
          {/* Falcon Analysis App Routes */}
          <Route path={ANALYSIS_ROUTES.HOME} element={<AnalysisHomePage />} />

          {/* Falcon Innovation Studio App Routes */}
          <Route
            path={INNOVATION_ROUTES.HOME}
            element={<DashboardHomePage />}
          />
          <Route
            path={INNOVATION_ROUTES.EXPERIMENT_SETUP}
            element={<ExperimentSetupRedirectPage />}
          />
          <Route
            path={EXPERIMENT_ROUTES.NEW_BASE}
            element={<ExperimentEditorLayout />}
          >
            <Route index element={<Navigate replace to="context" />} />
            <Route path="context" element={<ExperimentContextPage />} />
            <Route path="research" element={<ExperimentResearchPage />} />
            <Route path="prototype" element={<ExperimentPrototypePage />} />
            <Route path="tags" element={<ExperimentTaggingPage />} />
            <Route path="finalize" element={<ExperimentFinalizePage />} />
          </Route>
          <Route
            path={EXPERIMENT_ROUTES.EXISTING_BASE}
            element={<ExperimentEditorLayout />}
          >
            <Route index element={<Navigate replace to="context" />} />
            <Route path="context" element={<ExperimentContextPage />} />
            <Route path="research" element={<ExperimentResearchPage />} />
            <Route path="prototype" element={<ExperimentPrototypePage />} />
            <Route path="tags" element={<ExperimentTaggingPage />} />
            <Route path="finalize" element={<ExperimentFinalizePage />} />
          </Route>
          <Route
            path={INNOVATION_ROUTES.EXPERIMENT_REPORT}
            element={<ExperimentReportPage />}
          />
          <Route
            path={INNOVATION_ROUTES.PREVIEW_CUSTOMER}
            element={<ExperimentCustomerPreviewPage />}
          />
          <Route
            path={INNOVATION_ROUTES.PREVIEW_INTERNAL}
            element={<ExperimentCustomerPreviewPage />}
          />

          {/* Strategy Studio App Routes */}
          <Route path={STRATEGY_ROUTES.HOME} element={<StrategyHomePage />} />
        </Route>
      </Route>

      {/* 404 Not Found */}
      <Route
        path="*"
        element={<Navigate to={TERMINAL_ROUTES.HOME} replace />}
      />
    </Routes>
  );
};

export default AppRouter;
